import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { de } from "date-fns/locale";

interface MiniMonthCalendarProps {
  value: Date;
  onChange: (date: Date) => void;
}

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function MiniMonthCalendar({ value, onChange }: MiniMonthCalendarProps) {
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(value));

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(visibleMonth), { locale: de });
    const end = endOfMonth(visibleMonth);
    const cells: Date[] = [];
    let cursor = start;
    while (cursor <= end || cells.length % 7 !== 0) {
      cells.push(cursor);
      cursor = addDays(cursor, 1);
      if (cells.length > 42) break;
    }
    return cells;
  }, [visibleMonth]);

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          aria-label="Vorheriger Monat"
          onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
          className="h-7 w-7 rounded-md text-ink-muted hover:bg-surface-warm hover:text-primary"
        >
          ‹
        </button>
        <span className="font-display text-body-sm font-semibold uppercase tracking-wider text-ink">
          {format(visibleMonth, "MMMM yyyy", { locale: de })}
        </span>
        <button
          type="button"
          aria-label="Nächster Monat"
          onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
          className="h-7 w-7 rounded-md text-ink-muted hover:bg-surface-warm hover:text-primary"
        >
          ›
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium uppercase tracking-wide text-ink-muted">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const inMonth = isSameMonth(day, visibleMonth);
          const isSelected = isSameDay(day, value);
          const today = isToday(day);
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onChange(day)}
              className={[
                "h-7 rounded-md text-xs transition-colors",
                inMonth ? "text-ink" : "text-ink-muted/50",
                isSelected
                  ? "bg-primary text-white"
                  : today
                    ? "bg-surface-warm font-semibold text-primary"
                    : "hover:bg-surface-warm",
              ].join(" ")}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => {
          const now = new Date();
          setVisibleMonth(startOfMonth(now));
          onChange(now);
        }}
        className="mt-3 w-full rounded-md border border-line py-1.5 text-xs font-medium text-ink-soft transition-colors hover:border-primary hover:text-primary"
      >
        Heute
      </button>
    </div>
  );
}
